<template>
  <div>
    <b-row>
      <b-col md="12">
        <div class="table-notes-title" v-bind:style="styleTitle">
          <div class="text-truncate t-header" v-if="Title">{{ Title }}</div>
          <div class="btn-group btn-group-sm float-sm-right" role="group" aria-label="Table options">
            <b-button
              :hidden="!CanInsert || TableIsReadOnly"
              size="sm"
              @click="addNewEmptyRecordHandler"
              class="table-title-btn"
              :disabled="this.editRowIndex !== -1"
            >
              {{ $t('Shared.Add') }}
            </b-button>
            <b-button 
              :hidden="!CanDelete || TableIsReadOnly" 
              size="sm" 
              @click="saveHandler" 
              class="table-title-btn"
              :disabled="this.editRowIndex === -1"
            >
              {{$t('Shared.Save')}}
            </b-button>
            <b-button
              :hidden="!CanInsert || TableIsReadOnly"
              size="sm"
              @click="cancelHandler"
              class="table-title-btn"
              :disabled="this.editRowIndex === -1"
            >
              {{ $t('Shared.BtnCancel') }}
            </b-button>
          </div>
        </div>

        <b-table
          :ref="refTable"
          :small="small"
          :sticky-header="stickyHeader"
          v-b-tooltip.hover
          selectable
          class="table table-sm medezTables noteTable cutescroll"
          :select-mode="fMultiSelect"
          :items="items"
          :fields="fields"
          :current-page="cPage"
          @row-selected="rowSelected"
          responsive
          :filter="filter"
          :filter-included-fields="filterOn"
          @filtered="filtered"
          v-bind:style="style"
          :id="controlID"
          :show-empty="showMessageIsEmpty"
          :empty-text="$t('MdTable.nRecShow')"
          @row-dblclicked="dblClickedHandler"
        >
          <!-- Actions cell template -->
          <template #cell(actions)="row">
            <template v-if="editRowIndex !== row.index">
              <!-- Edit button -->
              <span
                v-if="visibleEditIcon && !isReadOnly"
                :id="'EditIcon-' + row.index"
                class="rounded-circle tableActionBTN tableBTNtext ikon med-pen"
                :class="{ 'iconDisabled btndisabled': shouldDisableEdit(row.item) }"
                @click="editHandler(row.index, row.item)"
              ></span>

              <!-- Delete button -->
              <span
                v-if="visibleDeleteIcon && !isReadOnly"
                :id="'DelIcon-' + row.index"
                class="rounded-circle tableActionBTN tableBTNtext tableActionBTN-danger ikon med-bin"
                :class="{ 'iconDisabled btndisabled': shouldDisableDelete(row.item) }"
                @click="deleteHandler(row.index, row.item)"
              ></span>

              <!-- Custom actions template -->
              <template v-for="(action, idx) in actions">
                <b-tooltip
                    v-if="action.tooltip && isActionVisible(action, row.item)"
                    :key="'tooltip-' + idx"
                    :target="'customAction-' + action.key + '-' + row.index"
                    placement="top"
                    :delay="{ show: 1500, hide: 50 }"
                >
                    {{ action.tooltip }}
                </b-tooltip>

                <span
                    v-if="isActionVisible(action, row.item)"
                    :key="'action-' + idx"
                    :id="'customAction-' + action.key + '-' + row.index"
                    :class="[
                        'rounded-circle',
                        'tableActionBTN',
                        action.class || 'tableBTNtext',
                        'ikon',
                        action.icon || '',
                        { 'iconDisabled btndisabled': isActionDisabled(action, row.item) }
                    ]"
                    @click="() => onCustomActionClick(action, row.item, row.index)"
                ></span>
              </template>
            </template>
            <template v-else>
              <!-- Save button -->
              <span
                :id="'SaveIcon-' + row.index"
                class="rounded-circle tableActionBTN tableBTNtext tableActionBTN-danger ikon med-save"
                @click="saveHandler()"
              ></span>
              <!-- Save button -->
              <span
                :id="'CancelIcon-' + row.index"
                class="rounded-circle tableActionBTN tableBTNtext tableActionBTN-danger ikon med-adm-undo"
                @click="cancelHandler()"
              ></span>
            </template>
            <!-- END customs actions template -->
          
          </template>
          <!-- END Actions cell template -->

          <!-- Generic cell template -->
          <template v-slot:cell()="row">
            <!-- View mode template -->
            <template v-if="GetRowStatus(row.index) === 'view'">
              <span :style="row.field.cellStyle">
                {{row.field.formatter ? row.field.formatter(row.item[row.field.key], row.item) : row.item[row.field.key]}}
              </span>
            </template>
            <!-- END view mode template -->

            <!-- Edit mode template -->
            <template v-else>
              <!-- Custom cell -->
              <component
                v-if="getCustomColumnComponent(row.field)"
                :is="getCustomColumnComponent(row.field).component"
                v-model="row.item[row.field.key]"
                :row="row"
                v-bind="getDynamicProps(row)"
              />

              <!-- String cell -->
              <MdEdit
                v-else-if="row.field.dataType === 'string'"
                v-bind:TypeProp="'text'"
                v-bind:Name="row.field.key + '_' + row.index"
                v-model="row.item[row.field.key]"
                v-bind:isReadOnly="row.field.isReadOnly || TableIsReadOnly"
                v-bind:Tag="row.field.key + '_' + row.index"
                v-bind:Id="row.field.key + '_' + row.index"
                v-bind:classValueStyle="'grid-textlabel'"
                v-bind:classValueInvalidStyle="'grid-textlabel-invalid'"
                v-bind="getProps(row)"
                @blur="blurHandler(row.field.key, $event, row.index, row.item)"
                :ref="row.field.key + '_' + row.index"
              ></MdEdit>

              <!-- Date cell -->
              <MdEdit
                v-else-if="row.field.dataType === 'date'"
                v-bind:TypeProp="'date'"
                v-bind:Name="row.field.key + '_' + row.index"
                v-model="row.item[row.field.key]"
                v-bind:isReadOnly="row.field.isReadOnly || TableIsReadOnly"
                v-bind:Tag="row.field.key + '_' + row.index"
                v-bind:Id="row.field.key + '_' + row.index"
                v-bind:classValueStyle="'grid-textlabel'"
                v-bind:classValueInvalidStyle="'grid-textlabel-invalid'"
                v-bind="getProps(row)"
                @blur="blurHandler(row.field.key, $event, row.index, row.item)"
                :ref="row.field.key + '_' + row.index"
              ></MdEdit>

              <!-- decimal cell -->
              <MdEdit
                v-else-if="row.field.dataType === 'decimal'"
                v-bind:TypeProp="'number'"
                v-bind:Name="row.field.key + '_' + row.index"
                v-model="row.item[row.field.key]"
                v-bind:isReadOnly="row.field.isReadOnly || TableIsReadOnly"
                v-bind:Tag="row.field.key + '_' + row.index"
                v-bind:Id="row.field.key + '_' + row.index"
                v-bind:classValueStyle="'grid-textlabel'"
                v-bind:StepProp="'0.01'"
                v-bind:classValueInvalidStyle="'grid-textlabel-invalid'"
                v-bind="getProps(row)"
                @blur="blurHandler(row.field.key, $event, row.index, row.item)"
                :ref="row.field.key + '_' + row.index"
              ></MdEdit>

              <!-- Integer cell -->
              <MdEdit
                v-else-if="row.field.dataType === 'integer'"
                v-bind:TypeProp="'number'"
                v-bind:Name="row.field.key + '_' + row.index"
                v-model="row.item[row.field.key]"
                v-bind:isReadOnly="row.field.isReadOnly || TableIsReadOnly"
                v-bind:Tag="row.field.key + '_' + row.index"
                v-bind:Id="row.field.key + '_' + row.index"
                v-bind:classValueStyle="'grid-textlabel'"
                v-bind:StepProp="'1'"
                v-bind:classValueInvalidStyle="'grid-textlabel-invalid'"
                v-bind="getProps(row)"
                @blur="blurHandler(row.field.key, $event, row.index, row.item)"
                :ref="row.field.key + '_' + row.index"
              ></MdEdit>

              <!-- Lookup cell -->
              <div v-else-if="row.field.dataType === 'lookup'">
                <MdLookup
                    :row="row"
                    v-bind:Tag="row.field.key + '_' + row.index"
                    v-bind:RefCode="row.field.key + '_' + row.index + '_lookup'"
                    v-bind:IsDisabled="row.field.isReadOnly || TableIsReadOnly"
                    v-bind:classValueStyle="'grid-textlabel'"
                    v-bind:classValueInvalidStyle="'grid-textlabel-invalid'"
                    v-bind="getProps(row)"
                    :ref="row.field.key + '_' + row.index"
                />
              </div>

              <!-- Default cell -->
              <span :style="row.field.cellStyle" v-else>
                {{row.field.formatter ? row.field.formatter(row.item[row.field.key], row.item) : row.item[row.field.key]}}
              </span>
            </template>
            <!-- END edit mode template -->
            
          </template>
          <!-- END generic cell template -->
        
        </b-table>
      </b-col>
    </b-row>

    <MdGetLastAnswers
      v-if="showGetLastAnswers"
      v-bind:ShowPopup="showGetLastAnswers"
      @getCancel="onCancelGetLastAnswers($event)"
      @getOK="onOKGetLastAnswers($event)"
      v-bind:ControlId="controlID"
      v-bind:PatientCode="PatientCode"
      v-bind:TransDate="TransDate"
    />
  </div>
</template>

<script>
import ShowMessage from '@/components/messages/ShowMessage.js';
import { EventBus } from '@/event-bus.js';
import Utils from "@/common/utils";

export default {
  name: 'MdGrid',
  mixins: [ShowMessage],

  props: {
    Name: String,
    isReadOnly: Boolean,
    CanDelete: Boolean,
    CanInsert: Boolean,
    TableIsReadOnly: Boolean,

    fields: Array,
    value: Array,

    currentPage: Number,
    perPage: Number,

    pageChanged: Function,
    perPageFunction: Function,
    onFiltered: Function,
    validateRequired: Function,
    validateControl: Function,
    onSave: Function,
    onRowDblClicked: Function,
    onBlurCell: Function,

    Title: String,
    refTable: String,
    small: Boolean,
    stickyHeader: [Boolean, String],
    showMessageIsEmpty: Boolean,
    visibleDeleteIcon: Boolean,
    visibleEditIcon: Boolean,

    actions: {
      type: Array,
      default: () => []
    },
    customColumns: {
      type: Array,
      default: () => []
    },
    showError: {
      type: Boolean,
      default: true
    },
  },

  data() {
    return {
      showGetLastAnswers: false,
      style: { border: 'solid 1px #e0e0e0' },
      pPage: this.perPage,
      cPage: this.currentPage,
      filter: null,
      filterOn: [],
      fFields: [],
      fMultiSelect: 'single',
      items: this.value,
      controlID: this.Name ?? 'grid-' + Utils.generateUUID(),
      editRowIndex: -1,
      rowBackup: null,
      styleTitle: {
        backgroundColor: ''
      },
    }
  },

  watch: {
    value(newValue) {
      this.items = newValue;
    }
  },

  methods: {
    getDynamicProps(row) {
      const col = this.customColumns.find(c => c.key === row.field.key);

      if (!col) {
        return {};
      }

      if (typeof col.props === 'function') {
        const dynamicProps = col.props(row.item, row.index);
        return dynamicProps || {};
      }

      return col.props || {};
    },

    getProps(row) {
      const col = this.fields.find(c => c.key === row.field.key);

      if (!col) {
        return {};
      }

      if (typeof col.props === 'function') {
        const dynamicProps = col.props(row.item, row.index);
        return dynamicProps || {};
      }

      return col.props || {};
    },


    isActionVisible(action, item) {
      if (typeof action.visible === 'function') {
        try {
          return action.visible(item);
        } 
        catch (e) {
          return true;
        }
      }
      
      return action.visible !== false;
    },

    isActionDisabled(action, item) {
      if (typeof action.disabled === 'function') {
        try {
          return action.disabled(item);
        } 
        catch (e) {
          return false;
        }
      }

      return !!action.disabled;
    },

    onCustomActionClick(action, item, index) {
      if (this.isActionDisabled(action, item)) {
        return;
      }

      if (typeof action.handler === 'function') {
        action.handler(item, index);
      }
    },

    GetRowStatus(index) {
      return this.editRowIndex === index ? 'edit' : 'view';
    },

    addNewEmptyRecordHandler() {
      const newFields = {};
      this.fields.forEach(element => {
        if (element.key !== 'actions') {
          newFields[element.key] = element.defaultValue ? element.defaultValue : '';
        }
      });

      this.items.push(newFields);
      this.editRowIndex = this.items.length - 1;
      this.rowBackup = null;
      this.emitChanged(newFields);
    },

    async editHandler(index, item) {
      if (this.editRowIndex !== -1 && this.editRowIndex < this.items.length) {
        const result = await this.saveHandler();

        if (!result) {
          return;
        }
      }
      
      this.editRowIndex = index;
      this.rowBackup = {...item };
    },

    async deleteHandler(index) {
      if (index < this.items.length) {
        await this.$bvModal
          .msgBoxConfirm(this.$t('Msg.DeleteRecord'), {
                title: this.$tc('Shared.DelRecord'),
                okTitle: this.$t('Shared.TitleDelete'),
                cancelTitle: this.$t('Shared.BtnCancel'),
                footerClass: 'p-2',
                titleClass: 'modal-title modal-type-warning'
              })
              .then(value => {
                if (value) {
                  this.items.splice(index, 1);

                  this.editRowIndex = -1;
                  this.rowBackup = null;
                  this.emitChanged();
                }
              });
      }
    },

    async saveHandler() {
      let result = false;
      if (this.editRowIndex !== -1 && this.editRowIndex < this.items.length) {
        if (this.validate_control()) {

          if (this.validate_required()) {
            result = true;

            if (this.onSave) {
              result = await this.onSave(this.item[this.editRowIndex])
            }

            if (result) {
              this.editRowIndex = -1;
              this.rowBackup = null;
            }

          } else if (this.showError) {
            this.showMessage(this.$tc('Msg.Error'),this.$t('Msg.CheckRedLabeledFields'),"errorAutoHide");

          }
        } else if (this.showError) {
          this.showMessage(this.$tc('Msg.Error'),this.$t('Msg.InvalidFieldValuesDetected'),"errorAutoHide");
        }
      }

      return result;
    }, 

    async cancelHandler() {
      if (this.editRowIndex !== -1 && this.editRowIndex < this.items.length) {
        await this.$bvModal
          .msgBoxConfirm(this.$t('Msg.UndoRecord'), {
                    title: this.$tc('Shared.UndoRecord'),
                    okTitle: this.$t('Shared.TitleYes'),
                    cancelTitle: this.$t('Shared.BtnCancel'),
                    footerClass: 'p-2',
                    titleClass: 'modal-title modal-type-warning'
                  })
                  .then(value => {
                    if (value) {
                      if (this.rowBackup) {
                        this.$set(this.items, this.editRowIndex, this.rowBackup);
                        this.emitChanged(this.rowBackup);
                      } else {
                        this.items.splice(this.editRowIndex, 1);
                        this.emitChanged();
                      }

                      this.$emit('input', this.items);
                      this.editRowIndex = -1;
                      this.rowBackup = null;
                    }
                  }
                );
      }
    },

    dblClickedHandler(item, index) {
      if (this.editRowIndex === -1) {
        if (this.onRowDblClicked) {
          this.onRowDblClicked(index, item);
        } else {
          this.editHandler(index, item);
        }
      }
    },

    blurHandler(target, value, index, item) {
      if (this.onBlurCell) {
        this.onBlurCell(target, value, index, item);
      }
    },

    validate_control() {
      if (this.validateControl && this.editRowIndex !== -1 && this.editRowIndex < this.items.length) {
        return this.validateControl(this.editRowIndex);
      }

      return true;
    },

    validate_required() {
      if (this.validateControl && this.editRowIndex !== -1 && this.editRowIndex < this.items.length) {
        return this.validateRequired(this.editRowIndex);
      }

      return true;
    },

    filtered(filteredItems) {
      this.onFiltered(filteredItems);
    },

    rowSelected() {},
    
    shouldDisableDelete(item) {
      return item?.isSystemType === true || this.editRowIndex !== -1;
    },
    
    shouldDisableEdit(item) {
      return item?.isSystemType === true || this.editRowIndex !== -1;
    },
    
    getCustomColumnComponent(field) {
      const col = this.customColumns.find(c => c.key === field.key);
      return col ? col : null;
    },

    setCellValid(target, index, value) {
      const refKey = `${target}_${index}`;
      if (this.$refs[refKey]) {
        this.$refs[refKey].setValid(value);
      }
    },

    setValid(state){
        this.styleTitle.backgroundColor = state ? '#dc3545 !important': ''
    },

    emitChanged(value) {
      var changed = {
          "Changed":true,
          "ComponentName":this.controlID,
          "Value" : value ?? this.items,
      } 

      EventBus.$emit('onChanged', changed);
    }
  },

  created() {
    if (!this.isReadOnly && this.fields && (this.visibleDeleteIcon || this.visibleEditIcon || this.customColumns.length !== 0)) {
      if (!this.fields.find(f => f.key === 'actions')) {
        this.fields.unshift({
          key: 'actions',
          label: this.$tc('Shared.ActionsTxt', 2),
          thClass: 'ActionsClass'
        });
      }
    }
  }
};
</script>

<style>
.grid-textlabel,
.grid-textlabel-invalid:before {
  padding: 0 0;
}

.grid-textlabel:before,
.grid-textlabel-invalid:before {
  content: attr(data-label);
  font-weight: bold;
  font-size: 10px;
  text-transform: uppercase !important;
  margin: 0 0 3px 10px !important;
  display: block;
  color: #3f3d4f;
}

.grid-textlabel-invalid {
  padding: 0.05em 0 0 0;
  color: #ff0d49;
  border: 1px solid red;
}

.iconDisabled {
  opacity: 0.4;
  pointer-events: none;
  cursor: not-allowed;
}
</style>